/** Without a click, key, scroll or touch for this long, a customer with the tab open has stepped away. */
export const IDLE_AFTER_MS = 2 * 60 * 1000

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll', 'touchstart'] as const

export interface TimeOnPageMeter {
  /** The whole seconds on page since the meter started. */
  seconds: () => number
  /** Stops counting; seconds() keeps returning the total it had reached. */
  stop: () => void
}

/**
 * Counts the time the customer is on the page: the tab is visible and they did something in the
 * last IDLE_AFTER_MS, so reading or waiting on a reply still counts but a tab left open doesn't.
 */
export function startTimeOnPageMeter(): TimeOnPageMeter {
  let countedMs = 0
  let countedUntil = performance.now()
  let lastActivity = countedUntil
  let visible = document.visibilityState === 'visible'

  function settle() {
    const now = performance.now()
    if (visible) countedMs += Math.max(0, Math.min(now, lastActivity + IDLE_AFTER_MS) - countedUntil)
    countedUntil = now
  }

  function onActivity() {
    settle()
    lastActivity = countedUntil
  }

  function onVisibilityChange() {
    settle()
    visible = document.visibilityState === 'visible'
    // Coming back to the tab is activity in itself.
    if (visible) lastActivity = countedUntil
  }

  // Capture, so a scroll inside any panel counts too: scroll events don't bubble.
  ACTIVITY_EVENTS.forEach((event) =>
    window.addEventListener(event, onActivity, { capture: true, passive: true })
  )
  document.addEventListener('visibilitychange', onVisibilityChange)

  return {
    seconds() {
      settle()
      return Math.floor(countedMs / 1000)
    },
    stop() {
      settle()
      visible = false
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onActivity, { capture: true }))
      document.removeEventListener('visibilitychange', onVisibilityChange)
    },
  }
}
